/**
 * Free-plan export watermark geometry (docs/spec/08-plan-features.md §1): a pill in the
 * bottom-right corner of the FINAL exported image (after downscaling and tone mapping).
 * All values in image px; text width must be measured by the platform (Inter SemiBold, glyph
 * advance widths, at `fontSize`).
 */
export const WATERMARK_TEXT = 'Made with Better Lights';
export const WATERMARK_BACKGROUND = { color: '#000000', alpha: 0.55 } as const;
export const WATERMARK_TEXT_COLOR = { color: '#FFFFFF', alpha: 0.92 } as const;

export interface WatermarkMetrics {
  /** Inset from the right and bottom edges: 2.5% of the short side. */
  inset: number;
  /** Pill height: max(18, 3.2% of the short side). */
  height: number;
  /** Corner radius = height / 2. */
  radius: number;
  /** Font size = 48% of the height. */
  fontSize: number;
  /** Horizontal text padding = 0.6 × height. */
  paddingX: number;
}

export function watermarkMetrics(width: number, height: number): WatermarkMetrics {
  const short = Math.min(width, height);
  const h = Math.max(18, short * 0.032);
  return { inset: short * 0.025, height: h, radius: h / 2, fontSize: h * 0.48, paddingX: h * 0.6 };
}

export interface WatermarkLayout extends WatermarkMetrics {
  /** Pill rectangle (bottom-right anchored). */
  rect: { x: number; y: number; width: number; height: number };
  /** Left x of the text run and the pill's vertical centre (centre the text on it). */
  textX: number;
  centerY: number;
}

/** Full layout given the text's advance width measured at `watermarkMetrics(...).fontSize`. */
export function watermarkLayout(width: number, height: number, textWidth: number): WatermarkLayout {
  const m = watermarkMetrics(width, height);
  const w = textWidth + 2 * m.paddingX;
  const x = width - m.inset - w;
  const y = height - m.inset - m.height;
  return { ...m, rect: { x, y, width: w, height: m.height }, textX: x + m.paddingX, centerY: y + m.height / 2 };
}
